import type { DeliveryGroupDraft } from './deliveryGroupTypes';
import { determineExecutionRelationsFromEdges } from './allocationEdgeDraft';
import type {
  AllocationEdgeDraft,
  FromStopDraftByUid,
  QuantityDraftByCargo,
  ToStopDraftByUid
} from './executionDraftTypes';
import type { LoadingUnloadingAllocationLink } from './loadingUnloadingLinkHelpers';
import type { RouteStopSaveRow } from './routeStopsSavePipeline';

export type ExecutionRelation = {
  cargoId: number;
  fromStopUid: string;
  toStopUid: string;
  quantity: number;
  fromPosition: number;
  toPosition: number;
};

export type StructuralRelation = {
  unloadingStopUid: string;
  unloadingPosition: number;
  parentLoadingPosition: number;
};

export type ExecutionAuthorityInput = {
  selectedIds: number[];
  fromStopDraft: FromStopDraftByUid;
  toStopDraft: ToStopDraftByUid;
  quantityDraft: QuantityDraftByCargo;
  positionByStopUid: Map<string, number>;
  /** RTE-X11.5c — when set, one relation per allocation edge (primary). */
  allocationEdges?: AllocationEdgeDraft[];
};

/** Execution authority: cargo allocations define loading→unloading edges. */
export function determineExecutionRelations(input: ExecutionAuthorityInput): ExecutionRelation[] {
  if (input.allocationEdges && input.allocationEdges.length > 0) {
    return determineExecutionRelationsFromEdges(
      input.allocationEdges,
      input.positionByStopUid
    );
  }

  const relations: ExecutionRelation[] = [];

  for (const cargoId of input.selectedIds) {
    const qty = Number(input.quantityDraft[cargoId] ?? 0);
    if (qty <= 0) continue;

    const fromStopUid = input.fromStopDraft[cargoId];
    const toStopUid = input.toStopDraft[cargoId];
    if (!fromStopUid || !toStopUid) continue;

    const fromPosition = input.positionByStopUid.get(fromStopUid);
    const toPosition = input.positionByStopUid.get(toStopUid);
    if (fromPosition === undefined || toPosition === undefined) continue;

    relations.push({
      cargoId,
      fromStopUid,
      toStopUid,
      quantity: qty,
      fromPosition,
      toPosition
    });
  }

  return relations;
}

/** Structural authority: parentLoadingPosition tree (backward compatibility only). */
export function determineStructuralRelations(
  saveRows: RouteStopSaveRow[]
): StructuralRelation[] {
  const loadingPositions = new Set(
    saveRows.filter((r) => r.type === 'loading').map((r) => r.position)
  );

  return saveRows
    .filter((r) => r.type === 'unloading')
    .map((r) => ({
      unloadingStopUid: r._stopUid ?? `pos-${r.position}`,
      unloadingPosition: r.position,
      parentLoadingPosition: Number(r.parentLoadingPosition ?? 0)
    }))
    .filter((r) => loadingPositions.has(r.parentLoadingPosition));
}

/** Build allocationLinks metadata from execution relations (not structural clones). */
export function executionRelationsToAllocationLinks(
  relations: ExecutionRelation[]
): LoadingUnloadingAllocationLink[] {
  const seen = new Set<string>();
  const links: LoadingUnloadingAllocationLink[] = [];

  for (const rel of relations) {
    const key = `${rel.fromPosition}->${rel.toPosition}`;
    if (seen.has(key)) continue;
    seen.add(key);
    links.push({ fromLoadingPosition: rel.fromPosition, toUnloadingPosition: rel.toPosition });
  }

  return links;
}

export type ExecutionOwnershipIssue = {
  code: 'MISSING_LOADING' | 'MISSING_UNLOADING' | 'CROSS_GROUP' | 'UNKNOWN_STOP';
  message: string;
  cargoId?: number;
};

export function validateExecutionOwnership(params: {
  groups: DeliveryGroupDraft[];
  relations: ExecutionRelation[];
  resolveOwnerGroupUid: (stopUid: string) => string | undefined;
  cargoApplicationById?: Record<number, number | undefined>;
  resolveGroupApplicationIds: (group: DeliveryGroupDraft) => Set<number>;
}): ExecutionOwnershipIssue[] {
  const issues: ExecutionOwnershipIssue[] = [];

  for (const rel of params.relations) {
    const fromGroup = params.resolveOwnerGroupUid(rel.fromStopUid);
    const toGroup = params.resolveOwnerGroupUid(rel.toStopUid);

    if (!fromGroup) {
      issues.push({
        code: 'UNKNOWN_STOP',
        cargoId: rel.cargoId,
        message: `Груз #${rel.cargoId}: точка погрузки не найдена в execution graph`
      });
      continue;
    }
    if (!toGroup) {
      issues.push({
        code: 'MISSING_UNLOADING',
        cargoId: rel.cargoId,
        message: `Груз #${rel.cargoId}: точка выгрузки не найдена в execution graph`
      });
      continue;
    }

    if (fromGroup !== toGroup) {
      const appId = params.cargoApplicationById?.[rel.cargoId];
      const ownerGroup = params.groups.find((g) => g.uid === fromGroup);
      if (appId != null && ownerGroup) {
        if (!params.resolveGroupApplicationIds(ownerGroup).has(Number(appId))) {
          issues.push({
            code: 'CROSS_GROUP',
            cargoId: rel.cargoId,
            message: `Груз #${rel.cargoId} нельзя привязать к точкам разных execution groups`
          });
        }
      }
    }
  }

  return issues;
}
